import {
  Header,
  SpaceBetween,
  Button,
  ColumnLayout,
  Tiles,
  TableProps,
  Table,
  FormField,
  Input,
  Box,
  CollectionPreferencesProps,
} from "@cloudscape-design/components-themed";
import { useEffect, useState, Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";
import {
  ADDRESS_APP,
  ADDRESS_NG,
  ADDRESS_TSR,
  COMMENT_MAX_LENGTH,
  RESULT_NG,
  RESULT_OK,
  LABELS,
} from "../constants";
import { getMessage } from "../../../features/error/messages";
import { isLengthValid } from "../../../utils/inputValidation";
import TableEmptyState from "../../UI/TableEmptyState";
import { COMMON_LABELS } from "../../default-constants";
import { DEFAULT_PAGE_SIZE } from "../../../configs/constants";
import {
  getHeaderCounterText,
  getIHRLabel,
  getPagedItems,
} from "../../../utils/common";
import Pagination from "../../UI/Pagination";
import Preference, { ContentOption } from "../../UI/Preference";
import { ConfirmationStoreInfo } from "../../../features/api-interfaces";
import {
  findRegisteredStore,
  getConfirmationAddressLabel,
  getApiConfirmationResult,
  getComment,
  getConfirmationResultDict,
  getAddress,
  isRequiresAddressConfirm,
  isRequiresRepNameConfirm,
  isSameStore,
} from "../util";
import { isEqual, isUndefined, uniq } from "lodash";
import { useCodelistHook } from "../../../app/useCodelistHook";
import { changeStyle } from "../../../utils/format";
import ConfirmationResultModal from "./ConfirmationResultModal";
import AddressSelectModal from "./AddressSelectModal";

interface IStore {
  registeredStoreList: ConfirmationStoreInfo[];
  storeList: ConfirmationStoreInfo[];
  setStoreList: Dispatch<SetStateAction<ConfirmationStoreInfo[] | undefined>>;
  resetStoreList: () => void;
}

/**
 * 個別取扱確認更新 店舗
 */
const Store = (props: IStore) => {
  const { registeredStoreList, storeList, setStoreList, resetStoreList } =
    props;
  const bulkConfirmationStatusItems = [
    {
      label: LABELS.BULK_CONFIRMATION_RESULT_OK,
      value: RESULT_OK.value,
    },
    {
      label: LABELS.BULK_CONFIRMATION_RESULT_NG,
      value: RESULT_NG.value,
    },
  ];
  const { individualHandlingReason } = useCodelistHook();
  const [pagedItems, setPagedItems] = useState<ConfirmationStoreInfo[]>([]);
  const [allItems, setAllItems] = useState<ConfirmationStoreInfo[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [bulkConfirmationStatus, setBulkConfirmationStatus] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [modalStore, setModalStore] = useState({} as ConfirmationStoreInfo);
  const [errorMsgList, setErrorMsgList] = useState<
    { store: ConfirmationStoreInfo; errorMsg: string }[]
  >([]);
  const [editingCommentStore, setEditingCommentStore] = useState<
    ConfirmationStoreInfo | undefined
  >(undefined);
  const [editingComment, setEditingComment] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    setPagedItems(
      getPagedItems(
        storeList,
        currentPageIndex,
        pageSize
      ) as ConfirmationStoreInfo[]
    );
    setAllItems(storeList);
  }, [storeList, currentPageIndex, pageSize]);

  /**
   * 確認結果の選択ボタンを押したときの処理.
   * @param {ConfirmationStoreInfo} item 更新対象の店舗の個別取扱情報
   */
  const confirmationResultOnClick = (item: ConfirmationStoreInfo) => {
    setModalStore(item);

    if (isRequiresAddressConfirm(item.individualHandlingReason)) {
      setIsAddressModalOpen(true);
    } else {
      setIsConfirmOpen(true);
    }
  };

  /**
   * コメントの入力欄にフォーカスが当たったときの処理.
   * @param {ConfirmationStoreInfo} editingStore 更新対象の店舗の個別取扱情報
   */
  const commentOnFocus = (editingStore: ConfirmationStoreInfo) => {
    setEditingCommentStore(editingStore);
    setEditingComment(editingStore.comment ?? "");
  };

  /**
   * コメントのイベントハンドラ.
   * @param {string} inputValue 入力されたコメント
   */
  const commentOnChange = (inputValue: string) => {
    if (editingCommentStore) {
      setEditingComment(inputValue);

      const errorMsg = isLengthValid(inputValue, COMMENT_MAX_LENGTH)
        ? ""
        : getMessage("KGRERR058", COMMENT_MAX_LENGTH).label;

      const index = errorMsgList.findIndex((errMsg) =>
        isSameStore(
          errMsg.store,
          editingCommentStore.applicationStatusNo,
          editingCommentStore.individualHandlingReason
        )
      );

      if (index !== -1) {
        const newErrorMsgList = [...errorMsgList];
        newErrorMsgList[index] = {
          ...newErrorMsgList[index],
          errorMsg,
        };
        setErrorMsgList(newErrorMsgList);
      } else {
        setErrorMsgList([
          ...errorMsgList,
          { store: editingCommentStore, errorMsg },
        ]);
      }
    }
  };

  /**
   * コメントの入力欄からフォーカスを外したときの処理.
   * @param {ConfirmationStoreInfo} item 更新対象の店舗の個別取扱情報
   */
  const commentOnBlur = (editingStore: ConfirmationStoreInfo) => {
    const index = storeList.findIndex((store) =>
      isSameStore(
        store,
        editingStore.applicationStatusNo,
        editingStore.individualHandlingReason
      )
    );

    if (index !== -1) {
      const newStoreList = [...storeList];
      newStoreList[index] = {
        ...newStoreList[index],
        comment: editingComment ?? "",
      };
      setStoreList(newStoreList);
    }

    setEditingCommentStore(undefined);
    setEditingComment(undefined);
  };

  const columnDefinitions = (
    items: ConfirmationStoreInfo[]
  ): TableProps.ColumnDefinition<ConfirmationStoreInfo>[] => {
    const columns: TableProps.ColumnDefinition<ConfirmationStoreInfo>[] = [
      {
        id: "individualHandlingReason",
        header: LABELS.INDIVIDUAL_HANDLING_REASON,
        cell: (item) =>
          getIHRLabel(individualHandlingReason, [
            item.individualHandlingReason,
          ]),
      },
      {
        id: "storeName",
        header: LABELS.STORE_NAME,
        cell: (item) => item.storeName,
      },
      {
        id: "corporateNumber",
        header: LABELS.CORPORATE_NUMBER,
        cell: (item) => (
          <>
            {item.individualCounselingTargetNo ? (
              <Link
                to={{
                  pathname: `/individual-counseling-project/${item.individualCounselingTargetNo}`,
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.corporateNumber}
              </Link>
            ) : (
              <>{item.corporateNumber}</>
            )}
          </>
        ),
      },
      {
        id: "companyName",
        header: LABELS.COMPANY_NAME,
        cell: (item) => item.companyName,
      },
      {
        id: "repName",
        header: LABELS.REP_NAME,
        cell: (item) => item.repName,
      },
      {
        id: "tradeName",
        header: LABELS.MERCHANT_NAME,
        cell: (item) => item.tradeName,
      },
      {
        id: "confirmationResult",
        header: LABELS.CONFIRMATION_RESULT,
        minWidth: 175,
        cell: (item) => (
          <SpaceBetween size={"xxxs"} direction={"horizontal"}>
            <Box float={"left"}>
              <div style={{ width: "60px", marginTop: "6px" }}>
                {isRequiresAddressConfirm(item.individualHandlingReason)
                  ? getConfirmationAddressLabel(
                      item.confirmationResult,
                      item.addressType
                    )
                  : getConfirmationResultDict(item.confirmationResult).label}
              </div>
            </Box>
            <Box float={"right"}>
              <Button
                variant={"normal"}
                onClick={() => confirmationResultOnClick(item)}
                disabled={
                  !isUndefined(
                    findRegisteredStore(
                      registeredStoreList,
                      item.applicationStatusNo,
                      item.individualHandlingReason
                    )
                  )
                }
              >
                {LABELS.SELECT}
              </Button>
            </Box>
          </SpaceBetween>
        ),
      },
      {
        id: "comment",
        header: LABELS.COMMENT,
        cell: (item) => (
          <FormField
            errorText={
              errorMsgList.find(
                ({ store }) =>
                  isEqual(
                    store.applicationStatusNo,
                    item.applicationStatusNo
                  ) &&
                  isEqual(
                    store.individualHandlingReason,
                    item.individualHandlingReason
                  )
              )?.errorMsg
            }
          >
            <Input
              value={
                editingCommentStore &&
                isEqual(
                  editingCommentStore.applicationStatusNo,
                  item.applicationStatusNo
                ) &&
                isEqual(
                  editingCommentStore.individualHandlingReason,
                  item.individualHandlingReason
                )
                  ? editingComment ?? item.comment ?? ""
                  : item.comment ?? ""
              }
              onFocus={() => commentOnFocus(item)}
              onChange={({ detail: { value } }) => commentOnChange(value)}
              onBlur={() => commentOnBlur(item)}
              disabled={
                !isUndefined(
                  findRegisteredStore(
                    registeredStoreList,
                    item.applicationStatusNo,
                    item.individualHandlingReason
                  )
                )
              }
            />
          </FormField>
        ),
      },
    ];

    // 店舗一覧内の個別取扱事由をカンマ区切りの文字列に変換する（重複は削除）
    const IHReasonList = uniq(
      items.map((item) => item.individualHandlingReason)
    ).join(",");

    // 要代表者確認の個別取扱事由が含まれている場合
    if (isRequiresRepNameConfirm(IHReasonList)) {
      columns.splice(
        6,
        0,
        {
          id: "repNameKanaApplication",
          header: LABELS.REP_NAME_KANA_APPLICATION,
          cell: (item) =>
            isRequiresRepNameConfirm(item.individualHandlingReason)
              ? item.repNameKanaApplication
              : COMMON_LABELS.HYPHEN,
        },
        {
          id: "repNameKanaTSR",
          header: LABELS.REP_NAME_KANA_TSR,
          cell: (item) =>
            isRequiresRepNameConfirm(item.individualHandlingReason)
              ? item.repNameKanaTSR
              : COMMON_LABELS.HYPHEN,
        }
      );
    }

    // 事業者住所確認の個別取扱事由が含まれている場合
    if (isRequiresAddressConfirm(IHReasonList)) {
      columns.splice(
        6,
        0,
        {
          id: "applicantPostalCode",
          header: LABELS.APPLICATION_POSTAL_CODE,
          cell: (item) => {
            if (isRequiresAddressConfirm(item.individualHandlingReason)) {
              // 事業者住所確認の場合
              let postalCode = "";

              // 登録済みの店舗の個別取扱情報を抽出する
              const registeredStore = findRegisteredStore(
                registeredStoreList,
                item.applicationStatusNo,
                item.individualHandlingReason
              );

              if (registeredStore) {
                // 住所が選択済みであればその住所を表示する
                const registeredAddress = getAddress(
                  registeredStore.addressList,
                  registeredStore.addressType
                );

                postalCode = registeredAddress?.postalCode ?? "";
              } else {
                // 住所が未選択であれば申請書の住所を表示する
                const appAddress = getAddress(
                  item.addressList,
                  Number(ADDRESS_APP.value)
                );
                postalCode = appAddress?.postalCode ?? "";
              }

              return changeStyle(postalCode);
            } else {
              return COMMON_LABELS.HYPHEN;
            }
          },
        },
        {
          id: "applicantAddress",
          header: LABELS.APPLICATION_ADDRESS,
          cell: (item) => {
            if (isRequiresAddressConfirm(item.individualHandlingReason)) {
              // 事業者住所確認の場合
              let address1 = "";
              let address2 = "";

              // 登録済みの店舗の個別取扱情報を抽出する
              const registeredStore = findRegisteredStore(
                registeredStoreList,
                item.applicationStatusNo,
                item.individualHandlingReason
              );

              if (registeredStore) {
                // 住所が選択済みであればその住所を表示する
                const registeredAddress = getAddress(
                  registeredStore.addressList,
                  registeredStore.addressType
                );

                address1 = registeredAddress?.address1 ?? "";
                address2 = registeredAddress?.address2 ?? "";
              } else {
                // 住所が未選択であれば申請書の住所を表示する
                const appAddress = getAddress(
                  item.addressList,
                  Number(ADDRESS_APP.value)
                );
                address1 = appAddress?.address1 ?? "";
                address2 = appAddress?.address2 ?? "";
              }

              return address1 + address2;
            } else {
              return COMMON_LABELS.HYPHEN;
            }
          },
        }
      );
    }

    return columns;
  };

  const options = columnDefinitions(storeList).map((column) => ({
    id: column.id,
    label: column.header,
  })) as ContentOption[];

  /**
   * 一括確認結果の入力ハンドラ.
   * @param {string} confirmationResultValue 確認結果value
   */
  const confirmationResultBulkCheck = (confirmationResultValue: string) => {
    setBulkConfirmationStatus(confirmationResultValue);

    const newItems = storeList.map((store) => {
      const registeredStore = findRegisteredStore(
        registeredStoreList,
        store.applicationStatusNo,
        store.individualHandlingReason
      );

      if (registeredStore) {
        // 確認結果が登録済みの個別取扱事由はこのハンドラで変更しない
        return store;
      }

      let addressType: number | undefined = undefined;
      if (isRequiresAddressConfirm(store.individualHandlingReason)) {
        // 事業者住所確認の場合
        if (isEqual(confirmationResultValue, RESULT_OK.value)) {
          // 一括確認結果OKの場合
          const tsrAddress = getAddress(
            store.addressList,
            Number(ADDRESS_TSR.value)
          );

          // TSR住所が取得できていればそれを設定する。なければ申請書の住所を採用する。
          addressType = isUndefined(tsrAddress)
            ? Number(ADDRESS_APP.value)
            : Number(ADDRESS_TSR.value);
        } else {
          // 一括確認結果NGの場合
          addressType = Number(ADDRESS_NG.value);
        }
      }

      return {
        ...store,
        confirmationResult: getApiConfirmationResult(confirmationResultValue),
        comment: getComment(confirmationResultValue, store.comment),
        addressType,
      } as ConfirmationStoreInfo;
    });

    setStoreList(newItems);
  };

  /**
   * クリアボタンを押したときの処理.
   */
  const onClear = () => {
    setBulkConfirmationStatus("");
    resetStoreList();
  };

  /**
   * ページサイズを変更したときの処理.
   * @param {CollectionPreferencesProps.Preferences} detail
   */
  const handleOnConfirm = (detail: CollectionPreferencesProps.Preferences) => {
    setPageSize(detail.pageSize!);
    setCurrentPageIndex(1);
  };

  return (
    <SpaceBetween direction={"vertical"} size={"s"}>
      <Table
        columnDefinitions={columnDefinitions(pagedItems)}
        items={pagedItems}
        empty={<TableEmptyState />}
        resizableColumns
        sortingDescending
        header={
          <SpaceBetween size={"xs"}>
            <Header>
              {LABELS.STORE}
              {getHeaderCounterText(allItems.length, pagedItems.length)}
            </Header>
            <ColumnLayout columns={3}>
              <Tiles
                items={bulkConfirmationStatusItems}
                value={bulkConfirmationStatus}
                onChange={({ detail: { value } }) =>
                  confirmationResultBulkCheck(value)
                }
              />
              <Button variant={"normal"} onClick={onClear}>
                {COMMON_LABELS.BTNS.CLEAR}
              </Button>
            </ColumnLayout>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            filteredItems={allItems}
            setCurrentPageIndex={setCurrentPageIndex}
            pageSize={pageSize}
            setPagedItems={setPagedItems}
          />
        }
        preferences={
          <Preference
            preferences={{ pageSize, visibleContent: [] }}
            options={options}
            handleOnConfirm={handleOnConfirm}
          />
        }
      />
      {isConfirmOpen && (
        <ConfirmationResultModal
          visible={isConfirmOpen}
          setVisible={setIsConfirmOpen}
          storeList={storeList}
          setStoreList={setStoreList}
          modalStore={modalStore}
        />
      )}
      {isAddressModalOpen && (
        <AddressSelectModal
          visible={isAddressModalOpen}
          setVisible={setIsAddressModalOpen}
          storeList={storeList}
          setStoreList={setStoreList}
          modalStore={modalStore}
          registeredStoreList={registeredStoreList}
        />
      )}
    </SpaceBetween>
  );
};

export default Store;
